import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import DocumentsVault from "@/components/DocumentsVault";
import StructureStatusCard from "@/components/StructureStatusCard";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  Clock,
  Music2,
  ShieldCheck,
  CalendarDays,
  ArrowUpRight,
  LogOut,
  Settings,
  Sparkles,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#D4AF37", "#FFB800", "#968032", "#F3C93E", "#A1A1AA"];

const PUBLISHING_LABELS = {
  not_started: "Not started",
  in_progress: "In progress",
  registered: "Registered",
};

const STATUS_COLORS = {
  not_started: "text-zinc-500 border-zinc-700",
  in_progress: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  registered: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-1 border text-[10px] font-mono uppercase tracking-widest ${STATUS_COLORS[status] || "text-zinc-500 border-zinc-700"}`}>
      {PUBLISHING_LABELS[status] || status}
    </span>
  );
}

export default function ArtistDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [royalties, setRoyalties] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [publishing, setPublishing] = useState({});
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, p, b, pub, bill] = await Promise.all([
          api.get("/me/royalties/summary"),
          api.get("/me/royalties"),
          api.get("/me/placements"),
          api.get("/me/bookings"),
          api.get("/me/publishing"),
          api.get("/billing/me"),
        ]);
        setSummary(s.data);
        setRoyalties(r.data);
        setPlacements(p.data);
        setBookings(b.data);
        setPublishing(pub.data || {});
        setBilling(bill.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const fmt = (n) =>
    "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const byPeriodData = summary
    ? Object.entries(summary.by_period).map(([k, v]) => ({ period: k, amount: Number(v) }))
    : [];
  const byTypeData = summary
    ? Object.entries(summary.by_type).map(([k, v]) => ({ name: k, value: Number(v) }))
    : [];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={TIDS.artistDashboard}>
      {/* Sub-header */}
      <div className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-2">
              / artist portal
            </div>
            <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter">
              Welcome back, {user?.artist_name || user?.name}
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              {summary?.count || 0} royalty entries tracked · {placements.length} placements logged
            </p>
          </div>
          <div className="flex items-center gap-3">
            {billing?.stripe_customer_id && (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
                    if (data?.url) window.location.href = data.url;
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not open subscription portal.");
                  }
                }}
                data-testid="manage-subscription-btn"
                className="btn-ghost"
              >
                <Settings size={14} /> Manage subscription
              </button>
            )}
            <Link to="/booking" className="btn-ghost">
              <CalendarDays size={14} /> Book a session
            </Link>
            <button onClick={handleLogout} className="btn-primary">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10 space-y-10">
        {/* KPI cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {[
            {
              label: "Total tracked",
              v: summary ? fmt(summary.total) : "—",
              icon: DollarSign,
              tid: TIDS.royaltyTotal,
              accent: true,
            },
            {
              label: "Received",
              v: summary ? fmt(summary.received) : "—",
              icon: TrendingUp,
              tid: TIDS.royaltyReceived,
            },
            { label: "Pending", v: summary ? fmt(summary.pending) : "—", icon: Clock, tid: TIDS.royaltyPending },
            { label: "Placements", v: placements.length, icon: Music2 },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <Link
                key={k.label}
                to="/royalties"
                data-testid={k.tid}
                className={`bg-[#0a0a0a] p-6 md:p-8 hover:bg-white/[0.03] transition-colors group ${k.accent ? "border-l-2 border-l-[var(--primary)]" : ""}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {k.label}
                  </div>
                  <Icon size={16} className={k.accent ? "text-[var(--primary)]" : "text-zinc-600"} strokeWidth={1.5} />
                </div>
                <div className={"font-heading font-black text-3xl md:text-4xl tracking-tighter " + (k.accent ? "text-[var(--primary)]" : "")}>
                  {k.v}
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-600 group-hover:text-[var(--primary)] transition-colors">
                  View details &rarr;
                </div>
              </Link>
            );
          })}
        </section>

        {/* Structure Onboarding — status + CTA */}
        <section>
          <StructureStatusCard />
        </section>

        {/* Split Sheets shortcut */}
        <section>
          <Link to="/split-sheets" className="block border border-white/10 bg-[#0a0a0a] p-6 md:p-8 hover:border-[var(--primary)]/40 transition-colors group" data-testid="split-sheets-cta">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-1">
                  // Split Sheets
                </div>
                <h3 className="font-heading font-black text-xl md:text-2xl tracking-tight text-white">
                  Lock in splits before the session ends.
                </h3>
                <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
                  Create a split sheet, email co-writers a one-click signing link, download the fully-executed PDF once everyone signs.
                </p>
              </div>
              <div className="text-[var(--primary)] group-hover:translate-x-1 transition-transform flex-shrink-0">
                <span className="font-mono text-xs uppercase tracking-widest">Open &rarr;</span>
              </div>
            </div>
          </Link>
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10">
          <div className="lg:col-span-2 bg-[#0a0a0a] p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  // earnings by period
                </div>
                <h3 className="font-heading font-bold text-xl mt-2">Royalty timeline</h3>
              </div>
            </div>
            <div className="h-[280px]">
              {byPeriodData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-zinc-600">
                  No data yet. Your consultant will populate this.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byPeriodData}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                    <XAxis dataKey="period" stroke="#71717A" fontSize={11} />
                    <YAxis stroke="#71717A" fontSize={11} />
                    <Tooltip
                      contentStyle={{
                        background: "#0a0a0a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#fff",
                        fontSize: 12,
                      }}
                      formatter={(v) => fmt(v)}
                    />
                    <Bar dataKey="amount" fill="#D4AF37" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-[#0a0a0a] p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              // by royalty type
            </div>
            <h3 className="font-heading font-bold text-xl mt-2 mb-4">Mix</h3>
            <div className="h-[240px]">
              {byTypeData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-zinc-600">
                  No data yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={byTypeData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={84} paddingAngle={2}>
                      {byTypeData.map((entry, i) => (
                        <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#0a0a0a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#fff",
                        fontSize: 12,
                      }}
                      formatter={(v) => fmt(v)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <ul className="mt-4 space-y-2">
              {byTypeData.map((d, i) => (
                <li key={d.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-zinc-400">
                    <span className="w-2 h-2" style={{ background: COLORS[i % COLORS.length] }} />
                    {d.name}
                  </span>
                  <span className="font-mono text-zinc-300">{fmt(d.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Publishing status */}
        <section className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                // publishing
              </div>
              <h3 className="font-heading font-bold text-xl mt-2 flex items-center gap-2">
                <ShieldCheck size={18} className="text-[var(--primary)]" strokeWidth={1.5} />
                Publishing & PRO status
              </h3>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-white/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">BMI</div>
              <StatusBadge status={publishing.bmi_status || "not_started"} />
              <div className="text-xs text-zinc-400 mt-3">ID: {publishing.bmi_id || "—"}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">ASCAP</div>
              <StatusBadge status={publishing.ascap_status || "not_started"} />
              <div className="text-xs text-zinc-400 mt-3">ID: {publishing.ascap_id || "—"}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">PRO Affiliation</div>
              <div className="text-base font-semibold">{publishing.pro_affiliation || "—"}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Publisher</div>
              <div className="text-base font-semibold">{publishing.publisher_name || "—"}</div>
            </div>
          </div>
        </section>

        {/* Royalties table */}
        <section className="bg-[#0a0a0a] border border-white/10">
          <div className="p-6 md:p-8 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                // royalties
              </div>
              <h3 className="font-heading font-bold text-xl mt-2">Recent royalty entries</h3>
            </div>
            <div className="text-xs text-zinc-500">{royalties.length} total</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="px-6 md:px-8 py-4">Type</th>
                  <th className="py-4">Source</th>
                  <th className="py-4">Period</th>
                  <th className="py-4">Status</th>
                  <th className="py-4 text-right pr-6 md:pr-8">Amount</th>
                </tr>
              </thead>
              <tbody>
                {royalties.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 md:px-8 py-10 text-center text-zinc-600 text-sm">
                      No entries yet.
                    </td>
                  </tr>
                )}
                {royalties.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 md:px-8 py-4 capitalize text-zinc-200">{r.type}</td>
                    <td className="py-4 text-zinc-300">{r.source}</td>
                    <td className="py-4 font-mono text-xs text-zinc-400">{r.period}</td>
                    <td className="py-4">
                      <span
                        className={
                          "inline-flex px-2 py-1 text-[10px] font-mono uppercase tracking-widest border " +
                          (r.status === "received"
                            ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                            : r.status === "paid_out"
                            ? "text-[var(--primary)] border-[var(--primary)]/40 bg-[var(--primary)]/10"
                            : "text-amber-300 border-amber-500/40 bg-amber-500/10")
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="py-4 pr-6 md:pr-8 text-right font-mono text-[var(--primary)]">
                      {fmt(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Placements + Bookings */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10">
          <div className="bg-[#0a0a0a] p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// placements</div>
            <h3 className="font-heading font-bold text-xl mt-2 mb-6">Platform placements</h3>
            <ul className="space-y-3">
              {placements.length === 0 && (
                <div className="text-zinc-600 text-sm">No placements logged yet.</div>
              )}
              {placements.map((p) => (
                <li key={p.id} className="flex items-start justify-between gap-4 border-b border-white/5 pb-3">
                  <div>
                    <div className="text-sm text-white">{p.track_title}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      <span className="text-[var(--primary)]">{p.platform}</span>
                      {p.playlist_or_show && ` · ${p.playlist_or_show}`}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                    {p.placed_on || "—"}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#0a0a0a] p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// consultations</div>
                <h3 className="font-heading font-bold text-xl mt-2">Bookings</h3>
              </div>
              <Link to="/booking" className="text-[var(--primary)] text-xs font-mono uppercase tracking-widest hover:text-white">
                <ArrowUpRight size={14} className="inline" /> Book
              </Link>
            </div>
            <ul className="space-y-3 mt-6">
              {bookings.length === 0 && (
                <div className="text-zinc-600 text-sm">No bookings yet.</div>
              )}
              {bookings.map((b) => (
                <li key={b.id} className="border-b border-white/5 pb-3">
                  <div className="text-sm text-white">{b.service}</div>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                    <span className="font-mono">{b.date} · {b.time_slot}</span>
                    <span
                      className={
                        "inline-flex px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest border " +
                        (b.status === "confirmed"
                          ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                          : b.status === "completed"
                          ? "text-[var(--primary)] border-[var(--primary)]/40 bg-[var(--primary)]/10"
                          : b.status === "cancelled"
                          ? "text-red-300 border-red-500/40 bg-red-500/10"
                          : "text-amber-300 border-amber-500/40 bg-amber-500/10")
                      }
                    >
                      {b.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Billing + Documents */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10">
          <BillingCard billing={billing} />
          <div className="lg:col-span-2 bg-[#0a0a0a]">
            <DocumentsVault
              listUrl="/me/documents"
              uploadUrl="/me/documents"
              deleteUrl={(id) => `/me/documents/${id}`}
              downloadUrl={(id) => `/me/documents/${id}/download`}
              heading="Your document vault"
              canUpload={true}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function BillingCard({ billing }) {
  const plan = billing?.plan || "free";
  const status = billing?.plan_status;
  const expires = billing?.plan_expires_at ? new Date(billing.plan_expires_at) : null;
  const planLabel = plan === "foundation" ? "Foundation" : plan === "catalogue" ? "Catalogue" : "Free";
  const isPaid = plan !== "free";

  return (
    <div className="bg-[#0a0a0a] p-6 md:p-8 flex flex-col" data-testid="billing-card">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// plan</div>
      <h3 className="font-heading font-bold text-xl mt-2 flex items-center gap-2">
        <Sparkles size={16} className="text-[var(--primary)]" strokeWidth={1.5} />
        Current plan
      </h3>
      <div className="mt-6 flex-1">
        <div className="font-heading font-black text-4xl tracking-tighter text-white">
          {planLabel}
        </div>
        <div className="mt-2 text-xs font-mono uppercase tracking-widest">
          {isPaid ? (
            <span className="text-emerald-300">{status || "active"}</span>
          ) : (
            <span className="text-zinc-500">No active plan</span>
          )}
        </div>
        {expires && (
          <div className="mt-4 text-xs text-zinc-400">
            Next billing: <span className="text-white font-mono">{expires.toLocaleDateString()}</span>
          </div>
        )}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <Link to="/pricing" className={isPaid ? "btn-ghost" : "btn-primary"}>
          {isPaid ? "Manage plan" : "Upgrade"} <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
